import {
  ArchiveTenantPayload,
  CreateTenantPayload,
  CreateTenantSecretMappingPayload,
  CreateTenantSecretMappingSuccessResponse,
  DecommissionPlan,
  DecommissionTenantPayload,
  FetchTenantPayload,
  FetchTenantSuccessResponse,
  InfraRequestState,
  RegisterServiceToTenantPayload,
  RequestTenantInfraPayload,
  Tenant,
  TenantInfraRequestResponse,
  UnarchiveTenantPayload,
  UpdateTenantPayload,
  VerifyTenantPayload,
} from 'TenantModule/types';
import { getApiGalaxyServer, putApiGalaxyServer, postApiGalaxyServer } from 'services';

export const fetchTenants = (body: FetchTenantPayload, config: ApiConfig = {}, token: string) => {
  return postApiGalaxyServer<FetchTenantPayload, FetchTenantSuccessResponse>(
    '/tenant/getAll',
    body,
    config,
    token,
  );
};

export const createTenant = (body: CreateTenantPayload, config: ApiConfig = {}, token: string) =>
  postApiGalaxyServer<CreateTenantPayload, Tenant>('/tenant', body, config, token);

export const updateTenant = (
  { tenantName, ...body }: UpdateTenantPayload,
  config: ApiConfig = {},
  token: string,
) =>
  putApiGalaxyServer<Omit<UpdateTenantPayload, 'tenantName'>, Tenant>(
    // Encoded like every other tenantName-in-path endpoint below — legacy names
    // aren't URL-safe by construction.
    `/tenant/${encodeURIComponent(tenantName)}`,
    body,
    config,
    token,
  );

export const archiveTenant = (
  {
    ...body
  }: {
    tenant: ArchiveTenantPayload['tenant'];
    force?: ArchiveTenantPayload['force'];
    meta: {
      name: ArchiveTenantPayload['archiver'];
    };
  },
  config: ApiConfig = {},
  token: string,
) => {
  return postApiGalaxyServer<typeof body, Tenant>(`/tenant/archive`, body, config, token);
};

/* ------------- Tenant lifecycle endpoints (RDM-1409 / GLX-1827) ------------- */

export const fetchTenant = (
  tenantName: Tenant['tenantName'],
  config: ApiConfig = {},
  token: string,
) => getApiGalaxyServer<Tenant>(`/tenant/${encodeURIComponent(tenantName)}`, config, token);

export const verifyTenant = (
  { tenantName, mockInfraFromTenant }: VerifyTenantPayload,
  config: ApiConfig = {},
  token: string,
) =>
  postApiGalaxyServer<{ mockInfraFromTenant?: string }, Tenant>(
    `/tenant/${encodeURIComponent(tenantName)}/verify`,
    mockInfraFromTenant ? { mockInfraFromTenant } : {},
    config,
    token,
  );

export const unarchiveTenant = (
  { tenantName }: UnarchiveTenantPayload,
  config: ApiConfig = {},
  token: string,
) =>
  postApiGalaxyServer<Record<string, never>, Tenant>(
    `/tenant/${encodeURIComponent(tenantName)}/unarchive`,
    {},
    config,
    token,
  );

export const fetchDecommissionPlan = (
  tenantName: Tenant['tenantName'],
  config: ApiConfig = {},
  token: string,
) =>
  getApiGalaxyServer<DecommissionPlan>(
    `/tenant/${encodeURIComponent(tenantName)}/decommission-plan`,
    config,
    token,
  );

export const decommissionTenant = (
  { tenantName, confirmTenantName }: DecommissionTenantPayload,
  config: ApiConfig = {},
  token: string,
) =>
  postApiGalaxyServer<{ confirmTenantName: string }, Tenant>(
    `/tenant/${encodeURIComponent(tenantName)}/decommission`,
    { confirmTenantName },
    config,
    token,
  );

/* ------------- Tenant stack PR requests (RDM-2277) ------------- */

/** Forces a GitHub refresh server-side when a non-terminal request exists. */
export const fetchTenantInfraRequest = (
  tenantName: Tenant['tenantName'],
  config: ApiConfig = {},
  token: string,
) =>
  getApiGalaxyServer<TenantInfraRequestResponse>(
    `/tenant/${encodeURIComponent(tenantName)}/infra-request`,
    config,
    token,
  );

export const requestTenantInfra = (
  { tenantName, ...body }: RequestTenantInfraPayload,
  config: ApiConfig = {},
  token: string,
) =>
  postApiGalaxyServer<Omit<RequestTenantInfraPayload, 'tenantName'>, InfraRequestState>(
    `/tenant/${encodeURIComponent(tenantName)}/infra-request`,
    body,
    config,
    token,
  );

export const createTenantSecretMapping = ({
  params,
  token,
  config = {},
}: BaseApiParams<CreateTenantSecretMappingPayload>) => {
  return postApiGalaxyServer<
    CreateTenantSecretMappingPayload,
    CreateTenantSecretMappingSuccessResponse
  >('/tenant/createServiceDefaultDatabaseCredential', params, config, token);
};

export const registerTenantService = ({
  params: body,
  token,
  config = {},
}: BaseApiParams<RegisterServiceToTenantPayload>) => {
  return postApiGalaxyServer('/tenant/registerServiceToTenant', body, config, token);
};
